import { Sorting } from '@devexpress/dx-react-grid'
import * as actions from '@returnmates/client-core/src/constants/actionTypes'
import {
  consolidationPackageError,
  packageIsTiedToConsolidation,
  returnDestinationMismatchError,
} from '@returnmates/client-core/src/constants/errors'
import { Consolidation, Package } from '@returnmates/client-core/src/graphql/generated/api'
import {
  getConsolidationPackagesCount,
  getConsolidationTableColumnWidth,
} from '@returnmates/client-core/src/selectors/admin'
import { SnackBarStatuses } from '@returnmates/client-core/src/type'
import errorMapper from '@returnmates/client-core/src/utils/errorMapper'
import { createAsyncAction } from '@returnmates/client-core/src/utils/reduxUtils'
import EditPackageReturnDestinationModal from '@returnmates/ui-core/src/components/CancellationModal/index'
import { FORM_ERROR, setIn } from 'final-form'
import { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { FLYOUT_SIDEBAR_TYPES, FlyoutSidebarTypes } from '../../constants/flyoutSidebar'
import { FlyoutSidebarContext } from '../../pages/Main'
import GridTable from '../GridTable'
import { TableColumn } from '../GridTable/types'
import RemovePackageModal from '../RemovePackageModal'
import AddNewPackageModal from './components/AddNewPackageModal'
import PickupCellFormatter from './components/PickupCellFormatter'
import PickupDetails from './components/PickupDetails'
import Toolbar from './components/Toolbar'
import {
  COLUMNS_GRID,
  DEFAULT_COLUMNS_WIDTH,
  FORMATTED_COLUMNS,
  FORMATTED_COLUMNS_NAMES,
} from './constants'
import { ConsolidationFilter, ConsolidationPackageInput } from './types'

interface Props {
  data: {
    [key: string]: string
  }[]
  columns?: TableColumn[]
  showDetails?: boolean
  totalCount?: number
  onSearchChange?: (search: string) => void
  onSortingChange?: (sort: Sorting) => void
  onCurrentPageChange?: (page: number) => void
  currentPageNumber?: number
  onPageSizeChange?: (size: number) => void
  currentPackagePage?: number
  setCurrentPackagePage?: (page: number) => void

  onFilterUpdate?: (val: ConsolidationFilter) => void
  isLoading: boolean
  updateOpenConsolidationDataRef?: (val: Consolidation) => void
  handleOpenAddConsolidationModal: () => void
  tableType: FlyoutSidebarTypes.EDIT_CONSOLIDATION | FlyoutSidebarTypes.EDIT_CONTAINER
}

function ConsolidationsTable({
  data,
  showDetails = true,
  columns = COLUMNS_GRID,
  totalCount,
  onSortingChange,
  onPageSizeChange,
  onCurrentPageChange,
  currentPageNumber,
  isLoading,
  onFilterUpdate,
  updateOpenConsolidationDataRef,
  handleOpenAddConsolidationModal,
  currentPackagePage,
  setCurrentPackagePage,
  tableType,
}: Props) {
  const { handleOpenSidebar, handleCloseSidebar, currentSidebarId } = useContext(
    FlyoutSidebarContext,
  )
  const [isInnerLoading, setIsInnerLoading] = useState(false)
  const [isOuterLoading, setIsOuterLoading] = useState(false)
  const [isReturnDestinationUpdatingLoading, setIsReturnDestinationUpdatingLoading] = useState(
    false,
  )
  const [currentRowData, setCurrentRowData] = useState<Consolidation | null>(null)

  const tableColumnWidth = useSelector(getConsolidationTableColumnWidth)
  const packagesCount = useSelector(getConsolidationPackagesCount)
  const [isRemovePackageModalOpen, setIsRemovePackageModalOpen] = useState(false)
  const [packageToRemove, setPackageToRemove] = useState<{
    consolidationId: string
    consolidationPackageId: string
  } | null>(null)

  const [packageSubmitError, setPackageSubmitError] = useState<{ [key: string]: string } | null>(
    null,
  )
  const [isNewPackageModalOpen, setIsNewPackageModalOpen] = useState(false)
  const [isReturnDestinationUpdateModalOpen, setIsReturnDestinationUpdateModalOpen] = useState(
    false,
  )
  const [packageIdToUpdateReturnDestination, setPackageIdToUpdateReturnDestination] = useState<
    string | null
  >(null)
  const [failedPackageIds, setFailedPackageIds] = useState<
    | {
        [key: string]: string
      }
    | {}
  >({})
  const [succedPackageIds, setSuccedPackageIds] = useState<string[]>([])
  const [currentPackagePageSize, setPagePackageSize] = useState<number>(0)

  const failedPackageIdsRef = useRef<string[]>([])
  const dispatch = useDispatch()

  const onCurrentPagePackageSize = useCallback(value => {
    setPagePackageSize(value)
  }, [])

  const onEditPackageClick = useCallback(
    (row: Package) => {
      if (currentSidebarId.current === row.id) {
        handleCloseSidebar()
      } else {
        handleOpenSidebar({ type: FLYOUT_SIDEBAR_TYPES.EDIT_PACKAGE, id: row.id })
      }
    },
    [currentSidebarId, handleCloseSidebar, handleOpenSidebar],
  )

  const resetScannedIdsState = useCallback(() => {
    setFailedPackageIds({})
    setSuccedPackageIds([])
    failedPackageIdsRef.current = []
  }, [])

  const closeNewPackageModal = useCallback(() => {
    setIsNewPackageModalOpen(false)
    setPackageSubmitError?.(null)
    resetScannedIdsState()
  }, [resetScannedIdsState])

  const closeReturnDestinationChangeModal = useCallback(() => {
    setIsReturnDestinationUpdateModalOpen(false)
  }, [])

  const handleCloseRemovePackageModal = useCallback(() => {
    setIsRemovePackageModalOpen(false)
  }, [])

  const fetchConsolidationPackages = useCallback(async () => {
    setIsOuterLoading(true)
    try {
      if (currentRowData?.id) {
        await createAsyncAction(
          dispatch,
          actions.getConsolidationPackages.request({
            where: {
              consolidationId: currentRowData?.id,
            },
            pagination: {
              size: currentPackagePageSize,
              page: currentPackagePage,
            },
          }),
        )
      }
    } catch (err) {
      const { message } = err as Error
      dispatch(
        actions.addSnackBar.request({
          type: SnackBarStatuses.ERROR,
          message: errorMapper(message || (err as string)),
        }),
      )
    } finally {
      setIsOuterLoading(false)
    }
  }, [currentPackagePageSize, currentPackagePage, dispatch, currentRowData?.id])

  const handlePackageDelete = useCallback(
    async ({ consolidationId, consolidationPackageId }: ConsolidationPackageInput) => {
      setIsInnerLoading(true)
      try {
        return await createAsyncAction(
          dispatch,
          actions.deleteConsolidationPackage.request({
            consolidationId,
            packageId: consolidationPackageId,
          }),
        )
      } catch (err) {
        const { message } = err as Error

        dispatch(
          actions.addSnackBar.request({
            type: SnackBarStatuses.ERROR,
            message: errorMapper(message || (err as string)),
          }),
        )
      } finally {
        closeNewPackageModal()
        handleCloseRemovePackageModal()
        setIsInnerLoading(false)
      }
    },
    [closeNewPackageModal, dispatch, handleCloseRemovePackageModal],
  )

  const handleRemovePackageConfirm = useCallback(async () => {
    if (packageToRemove) {
      await handlePackageDelete({
        consolidationId: packageToRemove?.consolidationId,
        consolidationPackageId: packageToRemove.consolidationPackageId,
      })

      await fetchConsolidationPackages()
    }
  }, [fetchConsolidationPackages, handlePackageDelete, packageToRemove])

  const updatePackageReturnDestination = useCallback(
    async (id?: string) => {
      const packageId = id || packageIdToUpdateReturnDestination

      const pack: Package = await createAsyncAction(
        dispatch,
        actions.getPackage.request({ id: packageId }),
      )
      if (packageId) {
        await createAsyncAction(dispatch, actions.packageTrackingList.request({ packageId }))
        await createAsyncAction(
          dispatch,
          actions.updatePackage.request({
            id: packageId,
            input: {
              tripId: pack?.tripId,
              returnDestinationId: currentRowData?.address.id,
            },
          }),
        )
      }
    },
    [currentRowData?.address.id, dispatch, packageIdToUpdateReturnDestination],
  )

  const onPackageCreate = useCallback(
    async ({
      consolidationId,
      consolidationPackageId,
      isMultiPackageLabel,
    }: ConsolidationPackageInput) => {
      try {
        const res = await createAsyncAction(
          dispatch,
          actions.createConsolidationPackage.request({
            consolidationId,
            packageId: consolidationPackageId,
          }),
        )

        if (res) {
          setSuccedPackageIds(prev => [...prev, consolidationPackageId as string])
          fetchConsolidationPackages()

          if (!isMultiPackageLabel) {
            closeNewPackageModal()
          }
        }
      } catch (err) {
        const { message } = err as Error

        setFailedPackageIds(prev => ({
          ...prev,
          [consolidationPackageId as string]: err || message,
        }))

        failedPackageIdsRef.current = [...failedPackageIdsRef.current, (err || message) as string]

        let errors = {}
        const setError = (key: string, value: string) => {
          errors = setIn(errors, key, value)
        }

        if (
          (message === packageIsTiedToConsolidation || err === packageIsTiedToConsolidation) &&
          !isMultiPackageLabel
        ) {
          const submitError = { [FORM_ERROR]: consolidationPackageError }

          if (submitError[FORM_ERROR] === consolidationPackageError) {
            setError('consolidationPackageId', consolidationPackageError)
          }

          setPackageSubmitError(errors)
        } else if (
          message?.includes(returnDestinationMismatchError(consolidationPackageId as string)) ||
          (typeof err === 'string' &&
            err?.includes(returnDestinationMismatchError(consolidationPackageId as string)))
        ) {
          setPackageIdToUpdateReturnDestination(consolidationPackageId as string)

          setIsReturnDestinationUpdateModalOpen(true)
        } else {
          dispatch(
            actions.addSnackBar.request({
              type: SnackBarStatuses.ERROR,
              message: errorMapper(message || (err as string)),
            }),
          )
        }
      }
    },
    [closeNewPackageModal, dispatch, fetchConsolidationPackages],
  )

  const editPackageReturnDestination = useCallback(async () => {
    try {
      setIsReturnDestinationUpdatingLoading(true)

      await updatePackageReturnDestination()

      closeReturnDestinationChangeModal()

      await onPackageCreate({
        consolidationId: currentRowData?.id as string,
        consolidationPackageId: packageIdToUpdateReturnDestination as string,
      })
    } catch (err) {
      const { message } = err as Error
      dispatch(
        actions.addSnackBar.request({
          type: SnackBarStatuses.ERROR,
          message: errorMapper(message || (err as string)),
        }),
      )
    } finally {
      setIsReturnDestinationUpdatingLoading(false)
    }
  }, [
    closeReturnDestinationChangeModal,
    currentRowData?.id,
    dispatch,
    onPackageCreate,
    packageIdToUpdateReturnDestination,
    updatePackageReturnDestination,
  ])

  const handleAddPackage = useCallback(
    async (values: ConsolidationPackageInput, withReplacing: boolean) => {
      if (withReplacing) {
        setIsOuterLoading(true)
        const packageConsolidationForDelete: { consolidationId: string } = await createAsyncAction(
          dispatch,
          actions.getPackageConsolidation.request({
            packageId: values.consolidationPackageId,
          }),
        )

        if (packageConsolidationForDelete) {
          const deleteRes = await handlePackageDelete({
            consolidationId: packageConsolidationForDelete.consolidationId,
            consolidationPackageId: values.consolidationPackageId,
          })

          if (deleteRes && values.consolidationPackageId) {
            await onPackageCreate(values)
            setIsOuterLoading(false)
          }
        }
      } else {
        if (Array.isArray(values.consolidationPackageId)) {
          values.consolidationPackageId.forEach(async (packageId, index, arr) => {
            await onPackageCreate({
              ...values,
              consolidationPackageId: packageId,
              isMultiPackageLabel: true,
            })

            if (!failedPackageIdsRef.current.length && index === arr.length - 1) {
              setTimeout(() => closeNewPackageModal(), 500)
            }
          })
        } else {
          onPackageCreate(values)
        }
      }
    },
    [closeNewPackageModal, dispatch, handlePackageDelete, onPackageCreate],
  )

  const onColumnWidthsChange = useCallback(
    newTableSize => dispatch(actions.setConsolidationTableColumnWidth.request(newTableSize)),
    [dispatch],
  )

  const columnSize = useMemo(() => (tableColumnWidth ? tableColumnWidth : DEFAULT_COLUMNS_WIDTH), [
    tableColumnWidth,
  ])

  const setEditableRow = useCallback(
    row => {
      if (currentSidebarId.current === row.id) {
        handleCloseSidebar()
      } else {
        dispatch(actions.setCurrentSidebarConsolidation.request(row))
        handleOpenSidebar({ type: FLYOUT_SIDEBAR_TYPES[tableType], id: row.id })

        setCurrentRowData(row)
        updateOpenConsolidationDataRef?.(row)
      }
    },
    [
      currentSidebarId,
      dispatch,
      handleCloseSidebar,
      handleOpenSidebar,
      tableType,
      updateOpenConsolidationDataRef,
    ],
  )

  useEffect(() => {
    if (currentPackagePageSize) {
      fetchConsolidationPackages()
    }
  }, [currentPackagePageSize, fetchConsolidationPackages])

  return (
    <>
      <Toolbar
        onFilterUpdate={onFilterUpdate}
        handleOpenAddConsolidationModal={handleOpenAddConsolidationModal}
      />
      <GridTable
        columns={columns || COLUMNS_GRID}
        data={data}
        isLoading={isLoading}
        formattedColumnNames={FORMATTED_COLUMNS_NAMES}
        formatterComponent={PickupCellFormatter}
        setEditableRow={setEditableRow}
        detailsComponent={PickupDetails}
        detailsProps={{
          setPackageToRemove,
          isNewPackageModalOpen,
          setIsNewPackageModalOpen,
          setIsRemovePackageModalOpen,
          currentRowData,
          onEditPackageClick,
          fetchConsolidationPackages,
          isOuterLoading,
          packagesCount,
          currentPackagePage,
          setCurrentPackagePage,
          currentPackagePageSize,
          onCurrentPagePackageSize,
        }}
        showDetails={showDetails}
        isNetworkFiltering
        showPaging
        showSorting
        editOnRowClick
        formattedColumns={FORMATTED_COLUMNS}
        totalCount={totalCount}
        onPageSizeChange={onPageSizeChange}
        onCurrentPageChange={onCurrentPageChange}
        currentPageNumber={currentPageNumber}
        onSortingChange={onSortingChange}
        defaultColumnWidths={columnSize}
        onColumnWidthsChange={onColumnWidthsChange}
      />
      <AddNewPackageModal
        isOpen={isNewPackageModalOpen}
        onClose={closeNewPackageModal}
        consolidationId={currentRowData?.id}
        onPackageCreate={handleAddPackage}
        validation={packageSubmitError}
        setPackageSubmitError={setPackageSubmitError}
        isLoading={isOuterLoading}
        failedPackageIds={failedPackageIds}
        succedPackageIds={succedPackageIds}
        resetScannedIdsState={resetScannedIdsState}
      />
      <EditPackageReturnDestinationModal
        width={488}
        isOpen={isReturnDestinationUpdateModalOpen}
        onClose={closeReturnDestinationChangeModal}
        onConfirm={editPackageReturnDestination}
        buttonLabelConfirm={'Update Return Destination'}
        buttonLabelCancel={'Cancel'}
        header={returnDestinationMismatchError(packageIdToUpdateReturnDestination)}
        isLoading={isReturnDestinationUpdatingLoading}
      />

      <RemovePackageModal
        isOpen={isRemovePackageModalOpen}
        onClose={handleCloseRemovePackageModal}
        onConfirm={handleRemovePackageConfirm}
        isLoading={isInnerLoading}
      />
    </>
  )
}

export default memo(ConsolidationsTable)
